import {
  useGetOrgCustomLabelsByOrgLazyQuery,
  useGetOrgCustomLabelsByOrgQuery,
  useGetRequirementByIdLazyQuery,
  useGetRequirementFeedbackStatsByStudentIdLazyQuery,
} from "graphql/__generated__/hooks";
import { useAppSelector } from "hooks/useAppSelector";
import { useAuthUser } from "hooks/useAuthUser";
import { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { organizationSelectedSelector } from "reduxStore/organization/selectors";
import { FeedbackType } from "types/feedback";
import { Requirement } from "types/requirement";
import { Resource } from "types/resource";
import { ROLES } from "types/user";
import { filterFeedbackStats, getFeedbackForChart } from "utils/feedback";

export const useRequirementDetail = () => {
  const { requirementId, organizationId } = useParams();
  const [getRequirementById, { data }] = useGetRequirementByIdLazyQuery();
  const [
    getRequirementFeedbackStatsByStudentId,
    { data: requirementFeedbackStatsData },
  ] = useGetRequirementFeedbackStatsByStudentIdLazyQuery();

  const [getOrgCustomLabelsByOrg, { data: orgCustomLabelsData }] =
    useGetOrgCustomLabelsByOrgLazyQuery();

  const organization = useAppSelector(organizationSelectedSelector);

  const customLabels = useMemo(() => {
    const list =
      orgCustomLabelsData?.organization_custom_label?.map((ocl: any) =>
        ocl?.custom_label ? ocl.custom_label : null,
      ) || [];
    return (list.filter(Boolean) as any[]) || [];
  }, [orgCustomLabelsData]);

  const { role, user } = useAuthUser();
  const isOrgView = !!organizationId;
  const feedbacks = useMemo(() => {
    return requirementFeedbackStatsData?.feedback || [];
  }, [requirementFeedbackStatsData]);

  const requirement = useMemo<Requirement>(
    () => data?.requirement_by_pk as Requirement,
    [data],
  );

  const competencies = useMemo<Requirement[]>(() => {
    if (data?.requirement_by_pk?.competencies) {
      return data?.requirement_by_pk?.competencies.map(
        (c) => c.competency,
      ) as Requirement[];
    }
    return [];
  }, [data]);

  const resources = useMemo<Resource[]>(() => {
    return (data?.requirement_by_pk?.resources?.map((r) => r.resource) ||
      []) as Resource[];
  }, [data]);

  const requirementFeedbackStats = useMemo(() => {
    if (!requirementFeedbackStatsData) return null;

    const feedbackData =
      (requirementFeedbackStatsData?.feedback &&
        filterFeedbackStats(
          requirementFeedbackStatsData?.feedback as FeedbackType[],
          true,
        )) ||
      [];

    if (feedbackData && feedbackData.length > 0) {
      const dates = getFeedbackForChart(feedbackData);

      return {
        totalFeedbacks: feedbackData.length,
        currentRate: Math.max(...feedbackData.map((stat) => stat.value)),
        data: dates,
      };
    }

    return {
      totalFeedbacks: feedbackData.length,
      currentRate: Math.max(...feedbackData.map((stat) => stat.value)),
      data: feedbackData,
    };
  }, [requirementFeedbackStatsData]);

  const [displayFeedback, setDisplayFeedback] = useState<boolean>(false);
  const [selectedFeedbackId, setSelectedFeedbackId] = useState<string | null>(
    null,
  );

  const fetchCustomLabels = (orgId: string) => {
    getOrgCustomLabelsByOrg({
      variables: { orgId: orgId },
    });
  };

  useEffect(() => {
    if (requirementId) {
      getRequirementById({ variables: { requirementId } });
    }
  }, [requirementId]);

  useEffect(() => {
    if (user?.role === ROLES.STUDENT && requirementId && user?.user_id) {
      getRequirementFeedbackStatsByStudentId({
        variables: { requirementId, studentId: user.user_id },
      });
    }
  }, [requirementId, user?.user_id]);

  useEffect(() => {
    if (organizationId) {
      fetchCustomLabels(organizationId);
    }
  }, [organizationId]);

  useEffect(() => {
    if (!organizationId && organization?.org_id) {
      fetchCustomLabels(organization?.org_id);
    }
  }, [organizationId, organization]);

  const onClickDot = (data: any) => {
    if (!data || !data.feedback_id) return;
    setSelectedFeedbackId(data.feedback_id);
    setDisplayFeedback(true);
  };

  return {
    requirement,
    requirementId,
    requirementFeedbackStats,
    resources,
    competencies,
    role,
    isOrgView,
    organizationId,
    onClickDot,
    displayFeedback,
    setDisplayFeedback,
    feedbacks,
    selectedFeedbackId,
    customLabels,
  };
};
